import { Capacitor, registerPlugin } from '@capacitor/core'
import { buildDefaultHeaders } from './push'

export const NATIVE_DOWNLOAD_CONTRACT_VERSION = 1
export const NATIVE_DOWNLOAD_SELECTOR = '[data-native-download-grant]'
export const NATIVE_DOWNLOAD_REQUEST_HEADER = 'X-Membergy-Native-Download'

const GRANT_ENDPOINT_PATH = '/file-transfer-grants'

let nativeDownloadPlugin = null
let clickHandler = null
let actionExecutedHandler = null
let livewireHookRegistered = false

function getNativeDownloadPlugin() {
  if (!nativeDownloadPlugin) {
    nativeDownloadPlugin = registerPlugin('NativeDownload')
  }

  return nativeDownloadPlugin
}

export function isNativeDownloadAvailable(capacitor = Capacitor) {
  try {
    return capacitor.isNativePlatform() && capacitor.isPluginAvailable('NativeDownload')
  } catch (_) {
    return false
  }
}

function isSafeFilename(filename) {
  const normalizedFilename = filename.trim()

  return normalizedFilename !== '.' &&
    normalizedFilename !== '..' &&
    filename.length <= 255 &&
    !/[\\/]/u.test(filename) &&
    ![...filename].some((character) => {
      const codePoint = character.codePointAt(0)

      return codePoint <= 31 || codePoint === 127
    })
}

/**
 * Validate the narrow server-to-web transfer contract before a bearer URL is
 * handed to platform code. Error messages deliberately contain no URL/token.
 */
export function validateNativeDownloadGrant(payload, locationObject = window.location) {
  if (!payload || typeof payload !== 'object' || payload.contractVersion !== NATIVE_DOWNLOAD_CONTRACT_VERSION) {
    throw new Error('Unsupported native download contract')
  }

  if (
    typeof payload.url !== 'string' ||
    typeof payload.filename !== 'string' ||
    payload.filename.trim() === '' ||
    !isSafeFilename(payload.filename) ||
    typeof payload.mimeType !== 'string' ||
    payload.mimeType.length > 127 ||
    !/^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/i.test(payload.mimeType) ||
    !Number.isSafeInteger(payload.size) ||
    payload.size < 1 ||
    typeof payload.expiresAt !== 'string' ||
    !Number.isFinite(Date.parse(payload.expiresAt))
  ) {
    throw new Error('Invalid native download metadata')
  }

  let transferUrl

  try {
    transferUrl = new URL(payload.url, locationObject.href)
  } catch (_) {
    throw new Error('Invalid native download URL')
  }

  if (
    transferUrl.origin !== locationObject.origin ||
    transferUrl.protocol !== 'https:' ||
    transferUrl.username !== '' ||
    transferUrl.password !== '' ||
    transferUrl.search !== '' ||
    transferUrl.hash !== '' ||
    !/^\/file-transfers\/[a-f0-9]{64}$/.test(transferUrl.pathname)
  ) {
    throw new Error('Rejected native download URL')
  }

  return {
    contractVersion: NATIVE_DOWNLOAD_CONTRACT_VERSION,
    url: transferUrl.href,
    filename: payload.filename,
    mimeType: payload.mimeType,
    size: payload.size,
    expiresAt: payload.expiresAt
  }
}

export function validateNativeDownloadGrantRequest(request, locationObject = window.location) {
  if (
    !request ||
    typeof request !== 'object' ||
    typeof request.endpoint !== 'string' ||
    typeof request.type !== 'string' ||
    !/^[a-z][a-z0-9-]{0,63}$/.test(request.type) ||
    !Number.isSafeInteger(request.resourceId) ||
    request.resourceId < 1
  ) {
    throw new Error('Invalid native download grant request')
  }

  let endpoint

  try {
    endpoint = new URL(request.endpoint, locationObject.href)
  } catch (_) {
    throw new Error('Invalid native download grant endpoint')
  }

  if (
    endpoint.origin !== locationObject.origin ||
    endpoint.pathname !== GRANT_ENDPOINT_PATH ||
    endpoint.search !== '' ||
    endpoint.hash !== '' ||
    endpoint.username !== '' ||
    endpoint.password !== ''
  ) {
    throw new Error('Rejected native download grant endpoint')
  }

  const payload = {
    type: request.type,
    resourceId: request.resourceId
  }

  if (Object.hasOwn(request, 'options')) {
    if (!request.options || typeof request.options !== 'object' || Array.isArray(request.options)) {
      throw new Error('Invalid native download grant options')
    }

    payload.options = { ...request.options }
  }

  return {
    endpoint: endpoint.href,
    payload
  }
}

export async function requestNativeDownloadGrant(
  request,
  {
    fetcher = window.fetch.bind(window),
    locationObject = window.location
  } = {}
) {
  const validatedRequest = validateNativeDownloadGrantRequest(request, locationObject)
  const response = await fetcher(validatedRequest.endpoint, {
    method: 'POST',
    headers: buildDefaultHeaders(),
    credentials: 'include',
    redirect: 'error',
    body: JSON.stringify(validatedRequest.payload)
  })

  if (!response.ok || response.status !== 201) {
    throw new Error(`Native download grant failed with status ${response.status}`)
  }

  let payload

  try {
    payload = await response.json()
  } catch (_) {
    throw new Error('Invalid native download grant response')
  }

  return validateNativeDownloadGrant(payload, locationObject)
}

export async function handOffNativeDownload(grant, plugin = getNativeDownloadPlugin()) {
  const result = await plugin.download({
    contractVersion: grant.contractVersion,
    url: grant.url,
    filename: grant.filename,
    mimeType: grant.mimeType,
    size: grant.size
  })

  if (result?.accepted !== true) {
    throw new Error('Native download was not accepted')
  }

  return result
}

function dispatchFailure(messages, eventTarget) {
  eventTarget.dispatchEvent(new CustomEvent('notify', {
    detail: {
      type: 'danger',
      title: messages.errorTitle || 'Download',
      message: messages.errorMessage || 'The download could not be started.'
    }
  }))
}

async function requestAndHandOff(request, dependencies) {
  const grant = await requestNativeDownloadGrant(request, dependencies)
  await handOffNativeDownload(grant, dependencies.pluginFactory())
}

export function createNativeDownloadClickHandler({
  capacitor = Capacitor,
  pluginFactory = getNativeDownloadPlugin,
  fetcher = window.fetch.bind(window),
  locationObject = window.location,
  eventTarget = window
} = {}) {
  return async function handleNativeDownloadClick(event) {
    const link = event.target?.closest?.(NATIVE_DOWNLOAD_SELECTOR)

    if (
      !link ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      !isNativeDownloadAvailable(capacitor)
    ) {
      return
    }

    event.preventDefault()

    if (link.getAttribute('aria-busy') === 'true') {
      return
    }

    link.setAttribute('aria-busy', 'true')

    try {
      await requestAndHandOff({
        endpoint: link.dataset.nativeDownloadGrant,
        type: link.dataset.nativeDownloadType,
        resourceId: Number(link.dataset.nativeDownloadResourceId)
      }, { fetcher, locationObject, pluginFactory })
    } catch (_) {
      dispatchFailure({
        errorTitle: link.dataset.nativeDownloadErrorTitle,
        errorMessage: link.dataset.nativeDownloadErrorMessage
      }, eventTarget)
    } finally {
      link.removeAttribute('aria-busy')
    }
  }
}

export function createNativeActionDownloadHandler({
  capacitor = Capacitor,
  pluginFactory = getNativeDownloadPlugin,
  fetcher = window.fetch.bind(window),
  locationObject = window.location,
  eventTarget = window
} = {}) {
  return async function handleNativeActionDownload(event) {
    const request = event.detail?.response?.nativeDownload

    if (!request || !isNativeDownloadAvailable(capacitor)) {
      return
    }

    try {
      await requestAndHandOff(request, { fetcher, locationObject, pluginFactory })
    } catch (_) {
      dispatchFailure(request, eventTarget)
    }
  }
}

export function addNativeDownloadRequestHeader(options) {
  options.headers = options.headers || {}
  options.headers[NATIVE_DOWNLOAD_REQUEST_HEADER] = '1'
}

function registerNativeLivewireHeader(livewire) {
  if (livewireHookRegistered || !livewire?.hook) {
    return
  }

  livewireHookRegistered = true
  livewire.hook('request', ({ options }) => addNativeDownloadRequestHeader(options))
}

export function initNativeDownloads({
  livewire = window.Livewire,
  capacitor = Capacitor,
  documentObject = document,
  eventTarget = window
} = {}) {
  if (!isNativeDownloadAvailable(capacitor)) {
    return
  }

  registerNativeLivewireHeader(livewire)

  if (!clickHandler && typeof documentObject !== 'undefined') {
    clickHandler = createNativeDownloadClickHandler({ capacitor, eventTarget })
    documentObject.addEventListener('click', clickHandler)
  }

  if (!actionExecutedHandler) {
    actionExecutedHandler = createNativeActionDownloadHandler({ capacitor, eventTarget })
    eventTarget.addEventListener('action-executed', actionExecutedHandler)
  }
}
